import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import proxy from "../../proxy";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Icon,
  Badge,
  useToast,
  Spinner,
  Center,
} from "@chakra-ui/react";
import {
  ArrowForwardIcon,
  SettingsIcon,
  UnlockIcon,
} from "@chakra-ui/icons";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loginTime, setLoginTime] = useState("");
  const [dashboardData, setDashboardData] = useState({
    wallets: 0,
    transactions: 0,
    stocks: 0,
    dividends: 0,
    totalPortfolio: 0,
    monthlyDividends: 0,
    totalDividendsPaid: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const time = localStorage.getItem("admin_login_time");
    if (time) {
      const date = new Date(time);
      setLoginTime(date.toLocaleString());
    }
    fetchDashboardData();
  }, []);

const fetchDashboardData = async () => {
  setIsLoading(true);
  try {
    const [walletsRes, transactionsRes, stocksRes, dividendsRes] = await Promise.all([
      axios.get(`${proxy}server_mts/admin/GetAllWallets.php`), // ✅ Acum include și price
      axios.get(`${proxy}server_mts/admin/GetAllOperations.php`),
      axios.get(`${proxy}server_mts/admin/GetAllStocks.php`),
      axios.get(`${proxy}server_mts/admin/GetAllDividends.php`),
    ]);

    const wallets = walletsRes.data || [];
    const transactions = transactionsRes.data || [];
    const stocks = stocksRes.data || [];
    const dividends = dividendsRes.data || [];

    console.log('=== DEBUG CRYPTO VALUE ===');
    console.log('Wallets with prices:', wallets);

    // ===== CRYPTO CALCULATIONS - Simplificat =====
    let cryptoValue = 0;

    wallets.forEach(wallet => {
      const amount = Number(wallet.amount || 0);
      const price = Number(wallet.price || 0); // ✅ Price vine deja din JOIN
      const value = amount * price;
      
      console.log(`${wallet.symbol}: ${amount} × $${price} = $${value.toFixed(2)}`);
      
      cryptoValue += value;
    });

    console.log('Total Crypto Value:', cryptoValue.toFixed(2));
    console.log('========================');

    // Pentru 24h change, poți folosi history_price_crypto
    // Deocamdată setăm la 0
    const dailyChangeDollar = 0;
    const dailyChangePercent = 0;

    // ===== STOCKS CALCULATIONS =====
    const stocksValue = stocks.reduce(
      (sum, s) => sum + Number(s.nr_shares) * Number(s.average_price_share),
      0
    );
    const totalShares = stocks.reduce((sum, s) => sum + Number(s.nr_shares), 0);
    const totalCommissions = stocks.reduce((sum, s) => sum + Number(s.commission), 0);
    const totalInvested = stocksValue + totalCommissions;

    // ===== DIVIDEND CALCULATIONS =====
    const annualDividends = stocks.reduce((sum, s) => {
      return sum + Number(s.div_share) * Number(s.nr_shares) * Number(s.div_per_year);
    }, 0);
    const monthlyDividends = annualDividends / 12;
    const totalDividendsPaid = stocks.reduce((sum, s) => sum + Number(s.paid_div), 0);
    const dividendYield = stocksValue > 0 ? (annualDividends / stocksValue) * 100 : 0;

    // ===== TRANSACTION CALCULATIONS =====
    const inflows = transactions.filter(t => Number(t.out_in) === 1).length;
    const outflows = transactions.filter(t => Number(t.out_in) === 0).length;
    
    const inflowAmount = transactions
      .filter(t => Number(t.out_in) === 1)
      .reduce((sum, t) => sum + Number(t.amount), 0);
    const outflowAmount = transactions
      .filter(t => Number(t.out_in) === 0)
      .reduce((sum, t) => sum + Number(t.amount), 0);
    const netFlow = inflowAmount - outflowAmount;

    // ===== PORTFOLIO CALCULATIONS =====
    const totalPortfolio = cryptoValue + stocksValue;
    const currentValue = totalPortfolio + totalDividendsPaid;
    const totalGainLoss = currentValue - totalInvested;
    const portfolioROI = totalInvested > 0 ? (totalGainLoss / totalInvested) * 100 : 0;

    setDashboardData({
      // Counts
      wallets: wallets.length,
      transactions: transactions.length,
      stocks: stocks.length,
      dividends: dividends.length,
      
      // Portfolio
      totalPortfolio,
      totalInvested,
      totalGainLoss,
      portfolioROI,
      
      // Crypto
      cryptoValue,
      dailyChangeDollar,
      dailyChangePercent,
      
      // Stocks
      stocksValue,
      totalShares,
      totalCommissions,
      
      // Dividends
      monthlyDividends,
      annualDividends,
      totalDividendsPaid,
      dividendYield,
      
      // Transactions
      inflows,
      outflows,
      netFlow,
    });
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
    toast({
      title: "Error loading dashboard",
      description: "Some data may not be available",
      status: "warning",
      duration: 3000,
      position: "top",
    });
  } finally {
    setIsLoading(false);
  }
};

  const handleLogout = async () => {
    try {
      await axios.post(`${proxy}server_mts/admin/Logout.php`, {}, { withCredentials: true });
    } catch {}
    sessionStorage.removeItem('admin_token');

    toast({
      title: "Logged out successfully",
      status: "info",
      duration: 2000,
      position: "top",
    });

    navigate("/admin/login");
  };

  // Cards pentru secțiunile admin - ✅ Acum cu date reale
  const adminSections = [
    {
      id: "wallets",
      title: "Crypto Wallets",
      icon: "💎",
      description: "Manage wallet addresses and balances",
      count: dashboardData.wallets,
      color: "#4FD1C5",
      route: "/admin/wallets",
    },
    {
      id: "transactions",
      title: "Transactions",
      icon: "💸",
      description: "Add, edit or delete transactions",
      count: dashboardData.transactions,
      color: "#48BB78",
      route: "/admin/transactions",
    },
      {
    id: "dividends",
    title: "Dividends", // ✅ SCHIMBĂ ORDINEA - pune dividends înainte de stocks
    icon: "💰",
    description: "Track dividend payments",
    count: dashboardData.dividends, // ✅ Count real din DB
    color: "#ECC94B",
    route: "/admin/dividends",
  },
    {
      id: "stocks",
      title: "Stocks",
      icon: "📊",
      description: "Manage stock portfolio",
      count: dashboardData.stocks,
      color: "#4299E1",
      route: "/admin/stocks",
    },
    {
      id: "milestones",
      title: "Milestones",
      icon: "🎯",
      description: "Set and track milestones",
      count: null, // Vom adăuga când facem Milestones Management
      color: "#9F7AEA",
      route: "/admin/milestones",
    },
    {
      id: "settings",
      title: "Settings",
      icon: "⚙️",
      description: "General configuration",
      count: null,
      color: "#FC8181",
      route: "/admin/settings",
    },
    {
      id: "insights",
      title: "AI Insights",
      icon: "🤖",
      description: "View daily & weekly AI-generated portfolio insights",
      count: null,
      color: "#9F7AEA",
      route: "/admin/insights",
    },
    {
      id: "chat",
      title: "Messages",
      icon: "💬",
      description: "Read and reply to visitor messages",
      count: null,
      color: "#38B2AC",
      route: "/admin/chat",
    },
  ];

  const [hoveredCard, setHoveredCard] = useState(null);

  if (isLoading) {
    return (
      <Box minH="100vh" bg="gray.900">
        <Center minH="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="teal.400" thickness="4px" />
            <Text color="gray.400">Loading dashboard...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.900">
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.95))"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        py={4}
        px={6}
        boxShadow="0 4px 20px rgba(0,0,0,0.3)"
      >
        <Container maxW="1400px">
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <Heading
                size="md"
                bgGradient="linear(to-r, teal.300, cyan.400)"
                bgClip="text"
              >
                🛡️ Admin Dashboard
              </Heading>
              <Text fontSize="xs" color="gray.500">
                Mattias Portfolio Management System
              </Text>
            </VStack>

            <HStack spacing={3}>
              <Badge colorScheme="green" px={3} py={1} borderRadius="full">
                ● Logged in
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                leftIcon={<UnlockIcon />}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="1400px" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Welcome Section */}
          <Box
            p={6}
            bg="rgba(26,32,44,0.6)"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            <HStack justify="space-between" align="start">
              <VStack align="start" spacing={2}>
                <Heading size="md" color="white">
                  Welcome back, Admin! 👋
                </Heading>
                <Text color="gray.400" fontSize="sm">
                  Last login: {loginTime || "Just now"}
                </Text>
              </VStack>
              <Button
                size="sm"
                variant="outline"
                colorScheme="teal"
                leftIcon={<SettingsIcon />}
                onClick={() => navigate("/admin/settings")}
              >
                Quick Settings
              </Button>
            </HStack>
          </Box>

          {/* Quick Stats - ✅ Date reale */}
        <Box>
  <Heading size="sm" color="gray.300" mb={4}>
    📊 Quick Overview
  </Heading>
  
  {/* CRYPTO SECTION */}
        <Box mb={6}>
            <HStack justify="space-between" align="center" mb={3}>
            <Text fontSize="sm" color="cyan.300" fontWeight="bold">
                💎 Crypto Portfolio
            </Text>
            <Badge colorScheme="cyan" fontSize="xs" px={2} py={0.5}>
                {dashboardData.wallets} wallets
            </Badge>
            </HStack>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
            <StatCard
                label="Total Crypto Value"
                value={`$${dashboardData.cryptoValue.toFixed(2)}`}
                subtext="Current holdings"
                color="cyan"
            />
            <StatCard
                label="24H Change"
                value={`${dashboardData.dailyChangePercent >= 0 ? '+' : ''}${dashboardData.dailyChangePercent.toFixed(2)}%`}
                subtext={`${dashboardData.dailyChangeDollar >= 0 ? '+' : ''}$${dashboardData.dailyChangeDollar.toFixed(2)}`}
                color={dashboardData.dailyChangePercent >= 0 ? "green" : "red"}
            />
            <StatCard
                label="Total Transactions"
                value={dashboardData.transactions}
                subtext={`${dashboardData.inflows} in · ${dashboardData.outflows} out`}
                color="orange"
            />
            <StatCard
                label="Net Flow"
                value={`${dashboardData.netFlow >= 0 ? '+' : ''}${dashboardData.netFlow.toFixed(4)}`}
                subtext="All time"
                color={dashboardData.netFlow >= 0 ? "green" : "red"}
            />
            </SimpleGrid>
        </Box>

        {/* STOCKS SECTION */}
        <Box mb={6}>
            <HStack justify="space-between" align="center" mb={3}>
            <Text fontSize="sm" color="purple.300" fontWeight="bold">
                📈 Stock Portfolio
            </Text>
            <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5}>
                {dashboardData.stocks} positions
            </Badge>
            </HStack>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
            <StatCard
                label="Total Stock Value"
                value={`$${dashboardData.stocksValue.toFixed(2)}`}
                subtext={`${dashboardData.totalShares.toFixed(2)} shares`}
                color="purple"
            />
            <StatCard
                label="Total Invested"
                value={`$${dashboardData.totalInvested.toFixed(2)}`}
                subtext={`+$${dashboardData.totalCommissions.toFixed(2)} fees`}
                color="yellow"
            />
            <StatCard
                label="Dividend Yield"
                value={`${dashboardData.dividendYield.toFixed(2)}%`}
                subtext="Annual return"
                color="green"
            />
            <StatCard
                label="Monthly Dividends"
                value={`$${dashboardData.monthlyDividends.toFixed(2)}`}
                subtext={`$${dashboardData.annualDividends.toFixed(2)}/year`}
                color="green"
            />
            </SimpleGrid>
        </Box>

        {/* PORTFOLIO OVERVIEW */}
        <Box>
            <HStack justify="space-between" align="center" mb={3}>
            <Text fontSize="sm" color="teal.300" fontWeight="bold">
                💼 Total Portfolio
            </Text>
            <Badge colorScheme="teal" fontSize="xs" px={2} py={0.5}>
                Combined
            </Badge>
            </HStack>
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
            <StatCard
                label="Total Portfolio Value"
                value={`$${dashboardData.totalPortfolio.toFixed(2)}`}
                subtext="Crypto + Stocks"
                color="teal"
            />
            <StatCard
                label="Portfolio ROI"
                value={`${dashboardData.portfolioROI >= 0 ? '+' : ''}${dashboardData.portfolioROI.toFixed(2)}%`}
                subtext={`${dashboardData.portfolioROI >= 0 ? 'Profit' : 'Loss'}: $${Math.abs(dashboardData.totalGainLoss).toFixed(2)}`}
                color={dashboardData.portfolioROI >= 0 ? "green" : "red"}
            />
            <StatCard
                label="Total Dividends Paid"
                value={`$${dashboardData.totalDividendsPaid.toFixed(2)}`}
                subtext="All time earnings"
                color="green"
            />
            <StatCard
                label="Active Positions"
                value={dashboardData.wallets + dashboardData.stocks}
                subtext={`${dashboardData.wallets} crypto + ${dashboardData.stocks} stocks`}
                color="blue"
            />
            </SimpleGrid>
        </Box>
        </Box>

          {/* Admin Sections */}
          <Box>
            <Heading size="sm" color="gray.300" mb={4}>
              🔧 Management Sections
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {adminSections.map((section) => (
                <Box
                  key={section.id}
                  position="relative"
                  onMouseEnter={() => setHoveredCard(section.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => navigate(section.route)}
                  cursor="pointer"
                >
                  {/* Glow effect */}
                  <Box
                    position="absolute"
                    inset="-2px"
                    borderRadius="xl"
                    bg={section.color}
                    opacity={hoveredCard === section.id ? 0.2 : 0}
                    filter="blur(15px)"
                    transition="0.3s"
                  />

                  {/* Card */}
                  <Box
                    bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
                    p={5}
                    borderRadius="xl"
                    border="2px solid"
                    borderColor={
                      hoveredCard === section.id
                        ? section.color
                        : "rgba(255,255,255,0.1)"
                    }
                    transition="all 0.3s"
                    transform={
                      hoveredCard === section.id
                        ? "translateY(-4px)"
                        : "translateY(0)"
                    }
                    boxShadow={
                      hoveredCard === section.id
                        ? `0 12px 30px ${section.color}44`
                        : "0 4px 15px rgba(0,0,0,0.3)"
                    }
                    position="relative"
                  >
                    <VStack align="start" spacing={3}>
                      {/* Icon + Count */}
                      <HStack justify="space-between" w="full">
                        <Text fontSize="3xl">{section.icon}</Text>
                        {section.count !== null && (
                          <Badge
                            colorScheme="teal"
                            fontSize="xs"
                            px={2}
                            py={0.5}
                            borderRadius="full"
                          >
                            {section.count}
                          </Badge>
                        )}
                      </HStack>

                      {/* Title */}
                      <Heading size="sm" color="white">
                        {section.title}
                      </Heading>

                      {/* Description */}
                      <Text fontSize="xs" color="gray.400" minH="32px">
                        {section.description}
                      </Text>

                      {/* Arrow */}
                      <HStack
                        spacing={2}
                        color={section.color}
                        opacity={hoveredCard === section.id ? 1 : 0.6}
                        transition="0.3s"
                      >
                        <Text fontSize="xs" fontWeight="bold">
                          Manage
                        </Text>
                        <Icon
                          as={ArrowForwardIcon}
                          transform={
                            hoveredCard === section.id
                              ? "translateX(4px)"
                              : "translateX(0)"
                          }
                          transition="0.3s"
                        />
                      </HStack>
                    </VStack>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

// Component pentru statistici rapide - ✅ Actualizat
const StatCard = ({ label, value, subtext, color = "teal" }) => (
  <Box
    p={4}
    bg="rgba(26,32,44,0.6)"
    borderRadius="lg"
    border="1px solid"
    borderColor="whiteAlpha.200"
    transition="all 0.3s"
    _hover={{
      borderColor: `${color}.400`,
      transform: "translateY(-2px)",
      boxShadow: `0 8px 20px rgba(0,0,0,0.3)`,
    }}
  >
    <Text fontSize="xs" color="gray.500" mb={1} fontWeight="semibold">
      {label}
    </Text>
    <Heading size="lg" color={`${color}.300`} mb={1}>
      {value}
    </Heading>
    {subtext && (
      <Text fontSize="xs" color="gray.400" fontWeight="medium">
        {subtext}
      </Text>
    )}
  </Box>
);

export default AdminDashboard;